import dayjs from 'dayjs'
import { z } from 'zod'
import ApiService from '../api.service'
import { axiosInstance } from '../axios'

export type CampanhaModo = 'toast' | 'modal' | 'badge'
export type CampanhaSeveridade = 'info' | 'success' | 'warning' | 'error'
export type CampanhaStatus = 'agendada' | 'ativa' | 'encerrada' | 'enviada'

export interface CampanhaAlvo {
    clienteIds: number[] | 'todos'
    categorias: number[] | 'todas'
}

export interface CampanhaBotao {
    label: string
    url: string
}

export interface CampanhaImagemEnvio {
    /** Gerado no editor (nanoid); é o que o token do corpo referencia. */
    id: string
    mime: string
    base64: string
}

export interface PublicarCampanha {
    titulo: string
    mensagem: string
    corpoMarkdown?: string
    modos: CampanhaModo[]
    severity: CampanhaSeveridade
    alvo: CampanhaAlvo
    /** Ausentes no envio imediato (`umaVez: true`). */
    inicio?: string
    fim?: string
    umaVez: boolean
    botao?: CampanhaBotao
    imagens?: CampanhaImagemEnvio[]
}

export interface Campanha extends Omit<PublicarCampanha, 'imagens'> {
    id: string
    imagens: { id: string; mime: string }[]
    criadoPor: number
    /** Ausente quando o usuário que publicou não existe mais. */
    autor?: { id: number; nome: string }
    createdAt: string
    encerradaEm?: string
    status: CampanhaStatus
    leitores: number
}

/**
 * Espelha o `@Matches` de `CampanhaBotaoDto`: rota interna que não começa com
 * '//' nem '/\' (viraria protocol-relative) ou URL http(s) sem userinfo (o '@'
 * permitiria disfarçar o host, ex: https://google.com@evil.com). Divergir daqui
 * só empurraria a recusa para um 400 sem campo.
 */
const REGEX_URL =
    // biome-ignore lint/suspicious/noControlCharactersInRegex: o intervalo de controle é intencional: a regex os EXCLUI da URL
    /^(\/(?![/\\])[^\s\x00-\x1F\x7F]*|https?:\/\/[^\s\x00-\x1F\x7F/@]+(\/[^\s\x00-\x1F\x7F]*)?)$/

export const campanhaSchema = z
    .object({
        titulo: z.string().min(1, 'Obrigatório').max(120),
        mensagem: z.string().max(280),
        corpoMarkdown: z
            .string()
            .max(20000, 'O corpo passa de 20.000 caracteres'),
        severity: z.enum(['info', 'success', 'warning', 'error']),
        inicio: z.string().optional(),
        fim: z.string().optional(),
        umaVez: z.boolean(),
        modos: z
            .array(z.enum(['toast', 'modal', 'badge']))
            .min(1, 'Escolha ao menos um modo de exibição'),
        clienteIds: z
            .array(z.number())
            .transform(v => (v.length === 0 ? 'todos' : v)),
        categorias: z.array(z.coerce.number()).refine(v => v.length > 0, {
            message: 'Escolha ao menos uma categoria',
        }),
        botaoLabel: z.string().max(20).optional().or(z.literal('')),
        botaoUrl: z
            .string()
            .regex(REGEX_URL, 'Use uma rota interna ou um endereço http(s)')
            .optional()
            .or(z.literal('')),
    })
    .refine(dados => dados.umaVez || (!!dados.inicio && !!dados.fim), {
        message: 'Informe início e fim da janela',
        path: ['inicio'],
    })
    .refine(
        dados =>
            dados.umaVez ||
            !dados.inicio ||
            !dados.fim ||
            dayjs(dados.fim).isAfter(dados.inicio),
        {
            message: 'O fim deve ser posterior ao início',
            path: ['fim'],
        },
    )
    .refine(dados => !dados.botaoLabel === !dados.botaoUrl, {
        message: 'Preencha rótulo e URL do botão, ou deixe os dois em branco',
        path: ['botaoUrl'],
    })

export type CreateCampanha = z.infer<typeof campanhaSchema>

export const campanhas = new ApiService<PublicarCampanha, Campanha>('campanhas')

/**
 * A imagem é binária e autenticada, então não passa pelos hooks do
 * `ApiService`. A URL sai de `campanhas.url` para herdar o prefixo de versão.
 * O interceptor de resposta devolve o próprio Blob quando `responseType` é
 * 'blob' — daí o cast, já que a tipagem do axios ainda promete `AxiosResponse`.
 */
export function obterImagemCampanha(campanhaId: string, imagemId: string) {
    return axiosInstance.get(
        `${campanhas.url}/${campanhaId}/imagens/${imagemId}`,
        { responseType: 'blob' },
    ) as unknown as Promise<Blob>
}
